import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { FaUserClock } from "react-icons/fa";
import {
  FiAlertTriangle,
  FiCalendar,
  FiCheck,
  FiClock,
  FiEdit2,
  FiHome,
  FiInfo,
  FiList,
  FiSearch,
  FiShield,
  FiTag,
  FiTrash2,
  FiUsers,
  FiX,
  FiZap,
} from "react-icons/fi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Modal } from "reactstrap";
import swal from "sweetalert";

import {
  applyClassToCharacters,
  isUrdu as isUrduText,
  LoadingSpinner,
} from "../../../constants/const";
import { GetSelectedLanguage } from "../../helper/Method";
import { addMember } from "../../member/ApiMember";
import { ApiEvent } from "../ApiEvent";
import AttendanceTimeline from "./AttendanceTimeline";
import {
  ASSIGNABLE_MEMBER_TYPES,
  avatarColor,
  formatDateTime,
  formatShortDateTime,
  getAccessStatus,
  getWindowRules,
  initials,
  parseSearchResult,
  toApiDate,
  toWallDate,
} from "./attendanceAccessHelpers";
import "./EventAttendanceAccess.css";

const SEARCH_DEBOUNCE_MS = 500;
const DATE_FORMAT = "dd MMM yyyy · hh:mm aa";

const Avatar = ({ id, name, small }) => (
  <span
    className={`aa-avatar ur${small ? " sm" : ""}`}
    style={{ background: avatarColor(id) }}
  >
    {initials(name)}
  </span>
);

const EventAttendanceAccessModal = ({ eventRecord, onClose }) => {
  const { t } = useTranslation();
  const isUrdu = GetSelectedLanguage() === "ur";
  const ur = isUrdu ? "ur" : "";

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [eventInfo, setEventInfo] = useState(null);
  const [accessList, setAccessList] = useState([]);

  const [searchMode, setSearchMode] = useState("name");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchDone, setSearchDone] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [selected, setSelected] = useState([]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [saving, setSaving] = useState(false);

  const searchRequest = useRef(0);

  const eventStart = eventInfo ? toWallDate(eventInfo.date) : null;
  const eventEnd = eventInfo
    ? toWallDate(eventInfo.endDate || eventInfo.date)
    : null;

  const errorMessage = (err) => {
    if (err?.response?.status === 403) return t("attendance_access_err_forbidden");
    const key = err?.response?.data;
    if (typeof key === "string" && key.startsWith("attendance_access_err_"))
      return t(key);
    return t("attendance_access_err_generic");
  };

  const loadAccessList = useCallback(() => {
    return ApiEvent()
      .getAttendanceAccess(eventRecord.id)
      .then((res) => {
        setEventInfo(res.data.event);
        setAccessList(
          res.data.users.map((u) => ({
            ...u,
            start: toWallDate(u.startDate),
            end: toWallDate(u.endDate),
          })),
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventRecord.id]);

  useEffect(() => {
    setLoading(true);
    loadAccessList()
      .catch((err) => setLoadError(errorMessage(err)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadAccessList]);

  const accessMemberIds = useMemo(
    () => new Set(accessList.map((a) => a.memberId)),
    [accessList],
  );

  // ----------------------------------------------------------------- search
  useEffect(() => {
    const term = query.trim();
    setFocusedIndex(-1);
    setSearchDone(false);
    if (!eventInfo || term.length < (searchMode === "name" ? 2 : 1)) {
      setResults([]);
      setSearching(false);
      return undefined;
    }

    const requestId = ++searchRequest.current;
    setSearching(true);
    const timer = setTimeout(() => {
      const api = addMember();
      const call =
        searchMode === "name"
          ? api
              .getUnitParticipants(eventInfo.unitId, encodeURIComponent(term))
              .then((res) => res.data.map(parseSearchResult))
          : api
              .getUnitParticipantsByRegNo(eventInfo.unitId, encodeURIComponent(term))
              .then((res) => [{ ...parseSearchResult(res.data), unitName: "" }]);

      call
        .catch(() => [])
        .then((items) => {
          if (requestId !== searchRequest.current) return;
          setResults(items);
          setSearching(false);
          setSearchDone(true);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, searchMode, eventInfo]);

  const isEligible = (member) =>
    !member.memberTypeName || ASSIGNABLE_MEMBER_TYPES.includes(member.memberTypeName);

  const addSelected = (member) => {
    if (!member || !isEligible(member)) return;
    setSelected((prev) =>
      prev.some((m) => m.id === member.id) ? prev : [...prev, member],
    );
    setQuery("");
    setResults([]);
  };

  const removeSelected = (id) => {
    setSelected((prev) => prev.filter((m) => m.id !== id));
    if (id === editingMemberId) setEditingMemberId(null);
  };

  const handleSearchKeyDown = (e) => {
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      addSelected(results[focusedIndex >= 0 ? focusedIndex : 0]);
    } else if (e.key === "Escape") {
      setQuery("");
    }
  };

  // ----------------------------------------------------------------- window
  const rules = eventInfo
    ? getWindowRules(startDate, endDate, eventStart, eventEnd)
    : {};
  const windowValid = Object.values(rules).every((r) => r === "ok");
  const windowInvalid = Object.values(rules).some((r) => r === "bad");
  const canSave = selected.length > 0 && windowValid && !saving;

  const applyEventTiming = () => {
    setStartDate(eventStart);
    setEndDate(eventEnd);
  };

  const filterStartTime = (time) => time <= eventEnd;
  const filterEndTime = (time) =>
    time >= eventStart && (!startDate || time > startDate);

  // ----------------------------------------------------------------- actions
  const handleSave = () => {
    if (!canSave) return;
    setSaving(true);
    const count = selected.length;
    ApiEvent()
      .saveAttendanceAccess({
        eventId: eventRecord.id,
        memberIds: selected.map((m) => m.id),
        startDate: toApiDate(startDate),
        endDate: toApiDate(endDate),
      })
      .then(() => loadAccessList())
      .then(() => {
        toast.success(
          t("attendance_access_saved", {
            value: count,
            from: formatShortDateTime(startDate),
            to: formatShortDateTime(endDate),
          }),
        );
        setSelected([]);
        setEditingMemberId(null);
      })
      .catch((err) => toast.error(errorMessage(err)))
      .finally(() => setSaving(false));
  };

  const handleEdit = (access) => {
    setSelected([
      {
        id: access.memberId,
        name: access.name,
        unitName: access.unitName,
        regNo: access.regNo,
      },
    ]);
    setStartDate(access.start);
    setEndDate(access.end);
    setEditingMemberId(access.memberId);
  };

  const handleRemove = (access) => {
    swal({
      title: t("attendance_access_remove_title"),
      text: t("attendance_access_remove_text", { value: access.name }),
      icon: "warning",
      className: isUrdu ? "ur" : "",
      buttons: [t("common_cancel"), t("attendance_access_remove_confirm")],
      dangerMode: true,
    }).then((confirmed) => {
      if (!confirmed) return;
      ApiEvent()
        .removeAttendanceAccess(access.id)
        .then(() => loadAccessList())
        .then(() => {
          toast.success(t("attendance_access_removed"));
          removeSelected(access.memberId);
        })
        .catch((err) => toast.error(errorMessage(err)));
    });
  };

  // ----------------------------------------------------------------- render
  const renderRule = (state, label) => (
    <li className={state}>
      <span className="aa-rule-icon">
        {state === "ok" ? "✓" : state === "bad" ? "✕" : "•"}
      </span>
      <span className={ur}>{label}</span>
    </li>
  );

  const renderSearchResults = () => {
    if (!query.trim()) return null;
    if (searching)
      return (
        <div className="aa-dropdown">
          <div className="aa-dd-empty">{t("attendance_access_searching")}</div>
        </div>
      );
    if (!searchDone) return null;
    if (!results.length)
      return (
        <div className="aa-dropdown">
          <div className="aa-dd-empty">{t("attendance_access_no_match")}</div>
        </div>
      );
    return (
      <div className="aa-dropdown">
        {results.map((m, index) => {
          const eligible = isEligible(m);
          const hasAccess = accessMemberIds.has(m.id);
          const picked = selected.some((s) => s.id === m.id);
          return (
            <div
              key={m.id}
              role="option"
              aria-selected={index === focusedIndex}
              aria-disabled={!eligible}
              className={`aa-dd-item${index === focusedIndex ? " focused" : ""}${
                eligible ? "" : " disabled"
              }`}
              onMouseEnter={() => setFocusedIndex(index)}
              onMouseDown={(e) => {
                e.preventDefault();
                addSelected(m);
              }}
            >
              <Avatar id={m.id} name={m.name} />
              <div className="aa-dd-who">
                <b>{applyClassToCharacters(m.name, "ur")}</b>
                {m.unitName && <small>{applyClassToCharacters(m.unitName, "ur")}</small>}
              </div>
              {m.regNo && <span className="aa-pill slate en">#{m.regNo}</span>}
              {!eligible ? (
                <span className={`aa-pill rose ${ur}`}>
                  {t("attendance_access_not_eligible")}
                </span>
              ) : picked ? (
                <span className={`aa-pill teal ${ur}`}>
                  <FiCheck /> {t("attendance_access_selected")}
                </span>
              ) : hasAccess ? (
                <span className={`aa-pill amber ${ur}`}>
                  <FiShield /> {t("attendance_access_has_access")}
                </span>
              ) : m.memberTypeName ? (
                <span className="aa-pill teal">{applyClassToCharacters(m.memberTypeName, "ur")}</span>
              ) : null}
            </div>
          );
        })}
        <div className={`aa-dd-foot ${ur}`}>{t("attendance_access_search_hint")}</div>
      </div>
    );
  };

  const renderBody = () => (
    <>
      <div className="aa-event-strip">
        <div className="aa-event-chip violet">
          <span className="aa-chip-icon">
            <FiTag />
          </span>
          <div>
            <small className={ur}>{t("attendance_access_ijtima_type")}</small>
            <b>{applyClassToCharacters(eventInfo.eventType, "ur")}</b>
          </div>
        </div>
        <div className="aa-event-chip teal">
          <span className="aa-chip-icon">
            <FiHome />
          </span>
          <div>
            <small className={ur}>{t("attendance_access_unit")}</small>
            <b>{applyClassToCharacters(eventInfo.unitName, "ur")}</b>
          </div>
        </div>
        <div className="aa-event-chip sky">
          <span className="aa-chip-icon">
            <FiCalendar />
          </span>
          <div>
            <small className={ur}>{t("attendance_access_starts")}</small>
            <b className="en">{formatDateTime(eventStart)}</b>
          </div>
        </div>
        <div className="aa-event-chip amber">
          <span className="aa-chip-icon">
            <FiClock />
          </span>
          <div>
            <small className={ur}>{t("attendance_access_ends")}</small>
            <b className="en">{formatDateTime(eventEnd)}</b>
          </div>
        </div>
      </div>

      <div className="aa-body">
        <div className="aa-cols">
          {/* Step 1: rufqa */}
          <div className="aa-card">
            <h4 className="aa-card-title">
              <span className="aa-num teal">1</span>
              <span className={ur}>{t("attendance_access_step_rufqa")}</span>
              <span className="aa-card-sub">
                <span>{applyClassToCharacters(eventInfo.unitName, "ur")}</span>{" "}
                <span className={ur}>{t("attendance_access_sub_units")}</span>
              </span>
            </h4>
            <div className="aa-seg" role="tablist">
              {["name", "regNo"].map((mode) => (
                <button
                  key={mode}
                  type="button"
                  role="tab"
                  aria-selected={searchMode === mode}
                  className={`${searchMode === mode ? "on" : ""} ${ur}`}
                  onClick={() => {
                    setSearchMode(mode);
                    setQuery("");
                  }}
                >
                  {t(mode === "name" ? "attendance_access_by_name" : "attendance_access_by_reg_no")}
                </button>
              ))}
            </div>
            <div className="aa-search">
              <div className="aa-input">
                <FiSearch />
                <input
                  type={searchMode === "name" ? "text" : "number"}
                  className={searchMode === "name" && (isUrduText(query) || (!query && isUrdu)) ? "ur" : "en"}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder={t(
                    searchMode === "name"
                      ? "attendance_access_search_name_placeholder"
                      : "attendance_access_search_reg_no_placeholder",
                  )}
                />
                {searching && <span className="aa-spin" />}
                {query && !searching && (
                  <button type="button" className="aa-clear" onClick={() => setQuery("")}>
                    <FiX />
                  </button>
                )}
              </div>
              {renderSearchResults()}
            </div>

            {selected.length ? (
              <>
                <div className="aa-chips">
                  {selected.map((m) => (
                    <span
                      key={m.id}
                      className={`aa-chip${accessMemberIds.has(m.id) ? " warn" : ""}`}
                    >
                      <Avatar id={m.id} name={m.name} small />
                      <span className="aa-chip-name">{applyClassToCharacters(m.name, "ur")}</span>
                      {m.regNo && <span className="aa-pill slate en">#{m.regNo}</span>}
                      {accessMemberIds.has(m.id) && (
                        <span className={`aa-pill amber ${ur}`}>
                          <FiAlertTriangle /> {t("attendance_access_has_access_update")}
                        </span>
                      )}
                      <button
                        type="button"
                        className="aa-chip-x"
                        onClick={() => removeSelected(m.id)}
                        aria-label="remove"
                      >
                        <FiX />
                      </button>
                    </span>
                  ))}
                </div>
                <div className={`aa-sel-count ${ur}`}>
                  {t("attendance_access_selected_count", { value: selected.length })}
                </div>
              </>
            ) : (
              <div className={`aa-chips-empty ${ur}`}>
                <FiUsers /> {t("attendance_access_no_selection")}
              </div>
            )}
          </div>

          {/* Step 2: window */}
          <div className="aa-card">
            <h4 className="aa-card-title">
              <span className="aa-num indigo">2</span>
              <span className={ur}>{t("attendance_access_step_window")}</span>
              <span className={`aa-card-sub ${ur}`}>{t("attendance_access_window_hint")}</span>
            </h4>
            <div className="aa-dt-grid">
              <div className="aa-dt">
                <label className={ur}>{t("attendance_access_access_starts")}</label>
                <div className={`aa-dt-input${rules.startBeforeEventEnd === "bad" ? " invalid" : ""}`}>
                  <FiCalendar />
                  <DatePicker
                    selected={startDate}
                    onChange={setStartDate}
                    showTimeSelect
                    timeIntervals={15}
                    timeFormat="hh:mm aa"
                    timeCaption="Time"
                    dateFormat={DATE_FORMAT}
                    maxDate={eventEnd}
                    filterTime={filterStartTime}
                    placeholderText={t("attendance_access_pick_datetime")}
                    className="en"
                    portalId="aa-datepicker-portal"
                    popperClassName="aa-datepicker-popper"
                    dayClassName={(d) =>
                      d >= new Date(eventStart).setHours(0, 0, 0, 0) && d <= eventEnd
                        ? "aa-event-day"
                        : undefined
                    }
                  />
                </div>
                {rules.startBeforeEventEnd === "bad" && (
                  <div className={`aa-field-error ${ur}`}>
                    <FiAlertTriangle />
                    {t("attendance_access_err_start_field", { value: formatDateTime(eventEnd) })}
                  </div>
                )}
              </div>
              <div className="aa-dt">
                <label className={ur}>{t("attendance_access_access_ends")}</label>
                <div
                  className={`aa-dt-input${
                    rules.endAfterEventStart === "bad" || rules.startBeforeEnd === "bad"
                      ? " invalid"
                      : ""
                  }`}
                >
                  <FiCalendar />
                  <DatePicker
                    selected={endDate}
                    onChange={setEndDate}
                    showTimeSelect
                    timeIntervals={15}
                    timeFormat="hh:mm aa"
                    timeCaption="Time"
                    dateFormat={DATE_FORMAT}
                    minDate={startDate && startDate > eventStart ? startDate : eventStart}
                    filterTime={filterEndTime}
                    openToDate={endDate || eventEnd}
                    placeholderText={t("attendance_access_pick_datetime")}
                    className="en"
                    portalId="aa-datepicker-portal"
                    popperClassName="aa-datepicker-popper"
                    dayClassName={(d) =>
                      d >= new Date(eventStart).setHours(0, 0, 0, 0) && d <= eventEnd
                        ? "aa-event-day"
                        : undefined
                    }
                  />
                </div>
                {rules.endAfterEventStart === "bad" ? (
                  <div className={`aa-field-error ${ur}`}>
                    <FiAlertTriangle />
                    {t("attendance_access_err_end_field", { value: formatDateTime(eventStart) })}
                  </div>
                ) : rules.startBeforeEnd === "bad" ? (
                  <div className={`aa-field-error ${ur}`}>
                    <FiAlertTriangle />
                    {t("attendance_access_err_order_field")}
                  </div>
                ) : null}
              </div>
            </div>
            <button type="button" className={`aa-linkbtn ${ur}`} onClick={applyEventTiming}>
              <FiZap /> {t("attendance_access_use_event_timing")}
            </button>
            <AttendanceTimeline
              eventStart={eventStart}
              eventEnd={eventEnd}
              start={startDate}
              end={endDate}
              invalid={windowInvalid}
              labelClassName={ur}
            />
            <ul className="aa-rules">
              {renderRule(
                rules.startBeforeEventEnd,
                t("attendance_access_rule_start", { value: formatShortDateTime(eventEnd) }),
              )}
              {renderRule(
                rules.endAfterEventStart,
                t("attendance_access_rule_end", { value: formatShortDateTime(eventStart) }),
              )}
              {renderRule(rules.startBeforeEnd, t("attendance_access_rule_order"))}
            </ul>
          </div>
        </div>

        <div className="aa-info">
          <FiInfo />
          <div>{applyClassToCharacters(t("attendance_access_info"), "ur")}</div>
        </div>

        {/* Existing access */}
        <div className="aa-card aa-existing">
          <h4 className="aa-card-title">
            <span className="aa-num violet">
              <FiUsers />
            </span>
            <span className={ur}>{t("attendance_access_existing")}</span>
            <span className="aa-pill indigo">{accessList.length}</span>
          </h4>
          {accessList.length === 0 ? (
            <div className={`aa-empty ${ur}`}>
              <FiList /> {t("attendance_access_none")}
            </div>
          ) : (
            <div className="aa-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th className={ur}>{t("attendance_access_col_rafiq")}</th>
                    <th className={ur}>{t("attendance_access_col_reg_no")}</th>
                    <th className={ur}>{t("attendance_access_col_window")}</th>
                    <th className={ur}>{t("attendance_access_col_status")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {accessList.map((a) => {
                    const status = getAccessStatus(a.start, a.end);
                    return (
                      <tr key={a.id} className={a.memberId === editingMemberId ? "editing" : ""}>
                        <td>
                          <div className="aa-who">
                            <Avatar id={a.memberId} name={a.name} />
                            <div>
                              <b>{applyClassToCharacters(a.name, "ur")}</b>
                              {a.unitName && <small>{applyClassToCharacters(a.unitName, "ur")}</small>}
                            </div>
                          </div>
                        </td>
                        <td>{a.regNo && <span className="aa-pill slate en">#{a.regNo}</span>}</td>
                        <td className="aa-window en">
                          {formatDateTime(a.start)}
                          <span className="aa-arrow">→</span>
                          {formatDateTime(a.end)}
                        </td>
                        <td>
                          <span className={`aa-pill status-${status} ${ur}`}>
                            ● {t(`attendance_access_status_${status}`)}
                          </span>
                        </td>
                        <td className="aa-actions">
                          <button
                            type="button"
                            className="aa-iconbtn sky"
                            title={t("attendance_access_edit_window")}
                            onClick={() => handleEdit(a)}
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            type="button"
                            className="aa-iconbtn rose"
                            title={t("attendance_access_remove")}
                            onClick={() => handleRemove(a)}
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="aa-foot">
        <div className={`aa-summary ${ur}`}>
          {canSave ? (
            <>
              <FiUsers />
              <b>{t("attendance_access_footer_summary", { value: selected.length })}</b>
              <span className="en">
                · {formatShortDateTime(startDate)} → {formatShortDateTime(endDate)}
              </span>
            </>
          ) : windowInvalid ? (
            <span className="aa-summary-error">{t("attendance_access_footer_fix")}</span>
          ) : (
            t("attendance_access_footer_hint")
          )}
        </div>
        <button type="button" className={`aa-btn ghost ${ur}`} onClick={onClose}>
          {t("common_cancel")}
        </button>
        <button
          type="button"
          className={`aa-btn primary ${ur}`}
          disabled={!canSave}
          onClick={handleSave}
        >
          <FiCheck /> {saving ? t("attendance_access_saving") : t("attendance_access_save")}
        </button>
      </div>
    </>
  );

  return (
    <Modal
      isOpen
      toggle={onClose}
      centered={false}
      size="xl"
      className="aa-modal-dialog"
      contentClassName="aa-modal"
      autoFocus={false}
    >
      <div dir={isUrdu ? "rtl" : "ltr"}>
        <div className="aa-head">
          <div className="aa-head-row">
            <div className="aa-head-icon">
              <FaUserClock />
            </div>
            <div>
              <h3 className={ur}>{t("attendance_access_title")}</h3>
              <p className={ur}>{t("attendance_access_subtitle")}</p>
            </div>
            <button type="button" className="aa-close" onClick={onClose} aria-label="close">
              <FiX />
            </button>
          </div>
        </div>
        {loading ? (
          <div className="aa-loading">{LoadingSpinner()}</div>
        ) : loadError ? (
          <div className={`aa-load-error ${ur}`}>
            <FiAlertTriangle /> {loadError}
          </div>
        ) : (
          renderBody()
        )}
      </div>
      <ToastContainer
        autoClose={3500}
        bodyClassName={GetSelectedLanguage()}
        closeOnClick
        pauseOnHover={false}
        position={isUrdu ? "top-left" : "top-right"}
      />
    </Modal>
  );
};

export default EventAttendanceAccessModal;
